import { useState, useEffect, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import PerfectScrollbar from 'react-perfect-scrollbar';
import 'react-perfect-scrollbar/dist/css/styles.css';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Avatar,
  TextField,
  TableSortLabel,
  FormControl,
  Select,
  MenuItem,
  ListItemText,
  Checkbox,
  Divider,
  Box,
} from '@mui/material';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { apiClient } from '../../api-client/index.js';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { DarkTooltip } from 'components/DarkTooltip';
import { snapToArray } from '../../utils';
import { Portlet, PortletHeader, PortletLabel, PortletContent } from '../../components/Portlets';
import { Cell } from '../../components/Cell';
import { useFirestore } from '../../utils/firebase';
import { InlineEdit } from '../../components/InlineEdit';
import { useAuth } from '../../auth';
import { useTenant } from '../../hooks/useTenant';
import { grey } from '../../theme/colors';

const MEMBER_ROLES = {
  MEMBER: 'Member',
  MANAGER: 'Manager',
  OFFICE_MANAGER: 'Office Manager',
  ACCOUNT_MANAGER: 'Account Manager',
  ARCHIVED: 'Archived',
  HARDWARE_MANAGER: 'Hardware Manager',
};

const TeamMembersTable = () => {
  const tenant = useTenant();
  const { user, getToken } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [orderDirection, setOrderDirection] = useState('desc');
  const [managers, setManagers] = useState([]);
  const [managerEmails, setManagerEmails] = useState([]);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [memberToArchive, setMemberToArchive] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  const database = useFirestore();

  const TOTAL_VACATION_DAYS = tenant?.vacation.totalDays;

  const getMembers = useCallback(() => {
    const conditions = [];
    switch (selectedRole) {
      case MEMBER_ROLES.MEMBER:
        conditions.push(where('roles.member', '==', true));
        break;
      case MEMBER_ROLES.MANAGER:
        conditions.push(where('roles.manager', '==', true));
        break;
      case MEMBER_ROLES.OFFICE_MANAGER:
        conditions.push(where('roles.officeManager', '==', true));
        break;
      case MEMBER_ROLES.ACCOUNT_MANAGER:
        conditions.push(where('roles.accountManager', '==', true));
        break;
      case MEMBER_ROLES.ARCHIVED:
        conditions.push(where('roles.archived', '==', true));
        break;
      case MEMBER_ROLES.HARDWARE_MANAGER:
        conditions.push(where('roles.hardwareManager', '==', true));
        break;
      default:
        break;
    }

    const queryUsers = query(
      collection(database, 'users'),
      where('tenant.id', '==', user.tenant.id),
      ...conditions,
    );

    getDocs(queryUsers)
      .then(snapshot => {
        const result = snapToArray(snapshot);
        let filteredResult = result;
        if (selectedRole === '') {
          filteredResult = result.filter(member => member.roles && !member.roles.archived);
        }

        setMembers(filteredResult);

        const loadedManagers = [];
        const loadedManagerEmails = [];
        result.forEach(member => {
          if (member.roles && member.roles.manager) {
            loadedManagerEmails.push(member.email);
            loadedManagers.push({
              firstName: member.firstName,
              lastName: member.lastName,
              email: member.email,
            });
          }
        });
        if (loadedManagerEmails.length > 0 && loadedManagers.length > 0) {
          setManagerEmails(loadedManagerEmails);
          setManagers(loadedManagers);
        }

        setFilteredMembers(filteredResult);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [database, user.tenant.id, selectedRole]);

  const updateMembers = update => {
    const result = filteredMembers.map(m => {
      if (m.id !== update.id) return m;
      return { ...m, ...update };
    });

    setFilteredMembers(result);

    getMembers();
  };

  const updateUser = async (id, data) => {
    try {
      const result = await apiClient('PATCH', `/users/${id}`, getToken, data);
      if (result?.status !== 'OK') {
        enqueueSnackbar(result?.message || 'Update failed', { variant: 'error' });
        return;
      }
      return updateMembers({ id, data });
    } catch {
      enqueueSnackbar('Update failed', { variant: 'error' });
    }
  };

  const getFirstAndLastName = email => {
    if (!email) return '';
    const manager = managers.find(m => m.email === email);

    return manager ? `${manager.firstName} ${manager.lastName}` : '';
  };

  const handleManagerChange = async (e, userId) => {
    const {
      target: { value },
    } = e;
    // selected managers emails
    const managersEmails = typeof value === 'string' ? value.split(',') : value;

    const selectedManagers = [];
    await Promise.all(
      managersEmails.map(async email => {
        const q = query(
          collection(database, 'users'),
          where('email', '==', email),
          where('tenant.id', '==', user.tenant.id),
        );

        const querySnapshot = await getDocs(q);
        let manager = {};

        // this list will always have one value because email is unique
        querySnapshot.forEach(doc => {
          manager = doc.data();
        });

        selectedManagers.push({
          firstName: manager.firstName,
          lastName: manager.lastName,
          email: manager.email,
        });
      }),
    );
    await updateUser(userId, {
      assignedManager: selectedManagers,
    });
    updateMembers({
      userId,
      assignedManager: selectedManagers,
    });
  };

  const handleArchiveMember = async (e, userId) => {
    const memberToArchive = members.find(member => member.id === userId);

    setMemberToArchive(memberToArchive);
    setDialogOpen(true);
  };

  const onDismiss = () => {
    setDialogOpen(false);
  };

  const handleRoleChange = async (e, userId) => {
    let newRoles;
    switch (e.target.value) {
      case MEMBER_ROLES.MEMBER:
        newRoles = { member: true };
        break;
      case MEMBER_ROLES.MANAGER:
        newRoles = { manager: true };
        break;
      case MEMBER_ROLES.ACCOUNT_MANAGER:
        newRoles = { accountManager: true };
        break;
      case MEMBER_ROLES.OFFICE_MANAGER:
        newRoles = { officeManager: true };
        break;
      case MEMBER_ROLES.HARDWARE_MANAGER:
        newRoles = { hardwareManager: true };
        break;
      case MEMBER_ROLES.ARCHIVED:
        newRoles = { archived: true };
        break;
      default:
        newRoles = { member: true };
        break;
    }

    await updateUser(userId, { roles: newRoles });
  };

  const handleSortRequest = () => {
    const isAscending = orderDirection === 'asc';
    setOrderDirection(isAscending ? 'desc' : 'asc');
  };

  const filterMembers = e => {
    setFilteredMembers(
      members.filter(
        member => member.displayName.toLowerCase().indexOf(e.target.value.toLowerCase()) !== -1,
      ),
    );
  };

  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  };

  const getComparator = (order, orderBy) =>
    order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);

  const stableSort = (rowArray, comparator) => {
    const stabilizedArray = rowArray.map((el, index) => [el, index]);
    stabilizedArray.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedArray.map(el => el[0]);
  };

  useEffect(() => {
    setLoading(true);
    getMembers();
  }, [getMembers, selectedRole]);

  const getOldVacation = oldVacationDays => oldVacationDays ?? 0;

  const getCurrentVacation = availableDays => availableDays ?? 0;

  const getCurrentRole = roles => {
    if (!roles) return MEMBER_ROLES.MEMBER;
    if (roles.member) {
      return MEMBER_ROLES.MEMBER;
    }
    if (roles.manager) {
      return MEMBER_ROLES.MANAGER;
    }
    if (roles.officeManager) {
      return MEMBER_ROLES.OFFICE_MANAGER;
    }
    if (roles.accountManager) {
      return MEMBER_ROLES.ACCOUNT_MANAGER;
    }
    if (roles.hardwareManager) {
      return MEMBER_ROLES.HARDWARE_MANAGER;
    }
    if (roles.archived) {
      return MEMBER_ROLES.ARCHIVED;
    }
    return MEMBER_ROLES.MEMBER;
  };

  const handleChange = event => {
    setSelectedRole(event.target.value);
  };

  const onArchive = () => {
    if (memberToArchive.roles && memberToArchive.roles.manager) {
      const promises = [];
      members.forEach(member => {
        if (member.assignedManager?.email === memberToArchive.email) {
          const promise = new Promise(() => {
            updateUser(member.id, {
              assignedManager: null,
            });
          });
          promises.push(promise);
        } else if (Array.isArray(member.assignedManager)) {
          member.assignedManager.forEach((manager, index) => {
            if (manager.email === memberToArchive.email) {
              member.assignedManager.splice(index, 1);
              const promise = new Promise(() => {
                updateUser(member.id, {
                  assignedManager: member.assignedManager.length ? member.assignedManager : null,
                });
              });
              promises.push(promise);
            }
          });
        }

        Promise.all(promises)
          .then(() => {
            handleRoleChange({ target: { value: 'Archived' } }, memberToArchive.id);
            setDialogOpen(false);
          })
          .catch(() => {
            setDialogOpen(false);
          });
      });
    } else {
      handleRoleChange({ target: { value: 'Archived' } }, memberToArchive.id);
      setDialogOpen(false);
    }
  };

  const roleOptions = [
    { value: MEMBER_ROLES.MEMBER, note: '(no admin role)' },
    { value: MEMBER_ROLES.MANAGER, note: '(receives requests only from assigned users)' },
    {
      value: MEMBER_ROLES.OFFICE_MANAGER,
      note: '(receives hardware requests and approved edu budget requests)',
    },
    { value: MEMBER_ROLES.ACCOUNT_MANAGER, note: '(receives all requests)' },
    { value: MEMBER_ROLES.HARDWARE_MANAGER, note: '(receives only hardware requests)' },
    {
      value: MEMBER_ROLES.ARCHIVED,
      note: '(archived members)',
      sx: { backgroundColor: grey[200] },
    },
  ];

  return (
    <Portlet sx={{ background: 'none', border: 'none', boxShadow: 'none' }}>
      <PortletHeader
        sx={{
          '@media (max-width:480px)': {
            display: 'table',
            paddingBottom: '1.5rem',
          },
        }}
        noDivider
      >
        <PortletLabel
          subtitle={`${members.length} in total`}
          title="Team Members"
          sx={{
            '@media (max-width:480px)': {
              '& h5': {
                width: '9rem',
                fontSize: '1.3rem',
              },
              '& h6': {
                marginLeft: '0rem',
                fontSize: '0.7rem',
                width: '3rem',
              },
            },
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
          <TextField
            label="Employee"
            onChange={filterMembers}
            sx={{
              marginRight: '2rem',
              '@media (max-width:480px)': {
                marginBottom: '1rem',
                marginTop: '0.5rem',
                width: '100%',
              },
            }}
            size="small"
            variant="standard"
          />
          <FormControl variant="standard">
            <Select
              value={selectedRole}
              onChange={handleChange}
              sx={{
                width: '10.5rem',
                '@media (max-width:480px)': {
                  width: '100%',
                },
              }}
              size="small"
              id="react-select"
              displayEmpty
              renderValue={selectedRole => {
                if (selectedRole === '') {
                  return <div>User Role</div>;
                }
                return selectedRole;
              }}
              inputProps={{ 'aria-label': 'Without label' }}
            >
              <MenuItem value="">
                <div>User Role</div>
              </MenuItem>

              {roleOptions.map(({ value, note, sx }) => (
                <MenuItem key={value} value={value} sx={sx}>
                  <Box sx={{ fontSize: '0.938rem' }}>
                    <ListItemText primary={value} />
                    <span>{note}</span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </PortletHeader>
      <PerfectScrollbar>
        <PortletContent isLoading={isLoading} noPadding>
          {members.length ? (
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Email</TableCell>
                  <DarkTooltip title="Available days are the current period unused vacation days. Old vacation days are from the previous period (valid until June 30).">
                    <TableCell key="availableDays">
                      <TableSortLabel active direction={orderDirection} onClick={handleSortRequest}>
                        Available days
                      </TableSortLabel>
                    </TableCell>
                  </DarkTooltip>
                  <TableCell>Old/Current Vacation</TableCell>

                  <TableCell sx={{ width: '12.5%' }}>Education budget (EUR)</TableCell>
                  <TableCell>Assigned Manager</TableCell>
                  <TableCell>User Role</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {stableSort(filteredMembers, getComparator(orderDirection, 'availableDays')).map(
                  ({
                    id,
                    displayName,
                    email,
                    availableDays,
                    oldVacationDays,
                    photoURL,
                    roles,
                    availableEduBudget,
                    assignedManager,
                  }) => {
                    const currentRole = getCurrentRole(roles);
                    // conversion for entries with one manager
                    let currentAssignedManagers = [];
                    if (assignedManager) {
                      currentAssignedManagers = Array.isArray(assignedManager)
                        ? assignedManager
                        : [assignedManager];
                    }

                    const currentAssignedManagerEmails = currentAssignedManagers.map(
                      manager => manager.email,
                    );
                    // (currentAssignedManagers);

                    return (
                      <TableRow hover key={id} data-cy={email}>
                        <Cell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar
                              sx={{ width: '2rem', height: '2rem', marginRight: '1rem' }}
                              src={photoURL}
                              variant="rounded"
                            />
                            <span>{displayName}</span>
                          </Box>
                        </Cell>

                        <Cell>{email}</Cell>
                        <Cell>{(availableDays || 0) + (oldVacationDays || 0)}</Cell>
                        <Cell>
                          <InlineEdit
                            id={id}
                            name="oldVacationDays"
                            value={oldVacationDays || 0}
                            onUpdate={updateUser}
                            min={0}
                            sx={{
                              '& .MuiInputBase-root': {
                                fontSize: 'inherit',
                                fontWeight: 'inherit',
                                lineHeight: 'inherit',
                              },
                              '& .MuiInput-input': {
                                fontSize: 'inherit',
                                fontWeight: 'inherit',
                                lineHeight: 'inherit',
                                padding: 0,
                              },
                            }}
                          />
                          {'/'}
                          <InlineEdit
                            id={id}
                            name="availableDays"
                            value={availableDays || 0}
                            onUpdate={updateUser}
                            sx={{
                              '& .MuiInputBase-root': {
                                fontSize: 'inherit',
                                fontWeight: 'inherit',
                                lineHeight: 'inherit',
                              },
                              '& .MuiInput-input': {
                                fontSize: 'inherit',
                                fontWeight: 'inherit',
                                lineHeight: 'inherit',
                                padding: 0,
                              },
                            }}
                          />
                        </Cell>

                        <Cell>
                          <InlineEdit
                            id={id}
                            name="availableEduBudget"
                            value={availableEduBudget || 0}
                            onUpdate={updateUser}
                            min={0}
                          />
                        </Cell>
                        <Cell>
                          <FormControl fullWidth>
                            <Select
                              MenuProps={{
                                anchorOrigin: {
                                  vertical: 'bottom',
                                  horizontal: 'left',
                                },
                                transformOrigin: {
                                  vertical: 'top',
                                  horizontal: 'left',
                                },
                                getContentAnchorEl: null,
                              }}
                              renderValue={selected =>
                                selected.map(manager => getFirstAndLastName(manager)).join(', ')
                              }
                              placeholder="Choose manager"
                              labelId={`select-manager-label-${id}`}
                              id={`select-manager-${id}`}
                              value={currentAssignedManagerEmails || []}
                              onChange={e => handleManagerChange(e, id)}
                              multiple
                              label="Manager"
                              data-cy="managerSelect"
                              sx={{ width: '15rem' }}
                              size="small"
                              variant="standard"
                            >
                              {managerEmails.map(manager => (
                                <MenuItem key={manager} value={manager}>
                                  <Checkbox
                                    checked={currentAssignedManagerEmails.indexOf(manager) > -1}
                                    color="primary"
                                  />
                                  <ListItemText primary={getFirstAndLastName(manager)} />
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Cell>
                        <Cell>
                          <FormControl fullWidth>
                            <Select
                              MenuProps={{
                                anchorOrigin: {
                                  vertical: 'bottom',
                                  horizontal: 'left',
                                },
                                transformOrigin: {
                                  vertical: 'top',
                                  horizontal: 'left',
                                },
                                getContentAnchorEl: null,
                              }}
                              id="role-select"
                              renderValue={selectedRole => selectedRole}
                              value={currentRole}
                              onChange={e =>
                                e.target.value === 'Archived'
                                  ? handleArchiveMember(e, id)
                                  : handleRoleChange(e, id)
                              }
                              label="Role"
                              size="small"
                              data-cy="roleSelect"
                              variant="standard"
                            >
                              {roleOptions
                                .filter(({ value }) => value !== MEMBER_ROLES.ARCHIVED)
                                .map(({ value, note, sx }) => (
                                  <MenuItem key={value} value={value} sx={sx}>
                                    <Box sx={{ fontSize: '0.938rem' }}>
                                      <ListItemText primary={value} />
                                      <span>{note}</span>
                                    </Box>
                                  </MenuItem>
                                ))}
                              <Divider />
                              <MenuItem
                                sx={{ backgroundColor: '#FEF6F4' }}
                                value={MEMBER_ROLES.ARCHIVED}
                              >
                                <div>
                                  <ListItemText
                                    primary="Archive Member"
                                    slotProps={{ primary: { style: { color: '#D94844' } } }}
                                  />
                                </div>
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Cell>
                      </TableRow>
                    );
                  },
                )}
              </TableBody>
            </Table>
          ) : (
            <Box sx={{ padding: 24, paddingTop: 0, fontWeight: 300, color: '#777' }}>
              There are no members in this team.
            </Box>
          )}
        </PortletContent>
      </PerfectScrollbar>
      <ConfirmationDialog
        id="confirmation-dialog-description"
        setDialogOpen={setDialogOpen}
        isDialogOpen={isDialogOpen}
        member={memberToArchive}
        onArchive={onArchive}
        onDismiss={onDismiss}
      />
    </Portlet>
  );
};

export default TeamMembersTable;
